import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { config } from 'Shared/Services/environment.service';
import haversine from 'haversine';
import * as React from 'react';
import { Paginated } from '../../../Shared/Services/Properties/Models/Paginated';
import { Property } from '../../../Shared/Services/Properties/Models/Property';
import { Action, State as QueryFilters, createQueryMapFilters } from '../Hooks/useQueryFilters';
import { usePropertiesQuery } from '../Repositories/usePropertiesQuery';
import { createMarkerClusters } from '../Utils/createMarkerClusters';
import { SRPMapMarkers } from './SRPMapMarkers';

export function createMapCenter(state: QueryFilters, data?: Paginated<Property>) {
  if (state.searchByMap) {
    return {
      lat: state.ceLat!,
      lng: state.ceLng!,
    };
  }

  if (data) {
    return data.map.center;
  }

  return undefined;
}

interface HideableProps extends React.HTMLAttributes<HTMLDivElement> {
  isHidden?: boolean;
}

export function Hidable({ isHidden = false, ...rest }: HideableProps) {
  return isHidden ? <React.Fragment>{rest.children}</React.Fragment> : <div {...rest} />;
}

interface Props {
  mini?: boolean;
  state: QueryFilters;
  dispatch: React.Dispatch<Action>;
}

export function SRPMap({ state, dispatch, mini = false }: Props) {
  const response = usePropertiesQuery(createQueryMapFilters(state));
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const mapRef = React.useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config('NX_NEXT_PUBLIC_GOOGLE_MAPS_KEY'),
  });

  const onMapLoad = React.useCallback((element: google.maps.Map) => {
    mapRef.current = element;
  }, []);

  const onDragEnd = React.useCallback(async () => {
    const bounds = mapRef.current?.getBounds();
    const center = mapRef.current?.getCenter();
    const zoom = mapRef.current?.getZoom();

    if (bounds && center) {
      const distanceInKm = haversine(
        {
          latitude: bounds.getNorthEast().lat(),
          longitude: bounds.getNorthEast().lng(),
        },
        {
          latitude: bounds.getSouthWest().lat(),
          longitude: bounds.getSouthWest().lng(),
        },
        { unit: 'km' },
      );

      dispatch({
        type: 'APPLY_FILTER',
        payload: {
          ceLat: center.lat(),
          ceLng: center.lng(),
          diagonal: distanceInKm * 0.3,
          zoom: zoom,
          searchByMap: true,
        },
      });
    }
  }, []);

  return (
    <Hidable isHidden={mini} className="hidden lg:block w-1/2 shrink-0">
      <Hidable isHidden={mini} className="sticky top-[96px] min-h-[calc(100vh-96px)] bg-zinc-100">
        {isLoaded ? (
          <GoogleMap
            zoom={state.zoom || 10}
            center={createMapCenter(state, response.data)}
            onLoad={onMapLoad}
            onTilesLoaded={() => setIsMapLoaded(true)}
            onDragEnd={onDragEnd}
            onZoomChanged={onDragEnd}
            mapContainerStyle={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            options={{
              gestureHandling: mini ? 'greedy' : 'auto',
              streetViewControl: false,
              keyboardShortcuts: false,
              scaleControl: true,
              clickableIcons: false,
              scrollwheel: false,
              zoomControl: !mini,
            }}
          >
            {!response.isLoading && !response.isError && isMapLoaded && (
              <SRPMapMarkers
                state={state}
                mapRef={mapRef}
                clusters={createMarkerClusters(response.data.results)}
              />
            )}
          </GoogleMap>
        ) : (
          <div>Loading..</div>
        )}
      </Hidable>
    </Hidable>
  );
}
